import React, { useState, useContext, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import styles from './UploadPage.module.css';
import { AuthContext } from '../../context/authContext';
import apiRequest from '../../lib/apiRequest';
import logger from '../../lib/logger';

const UploadPage = () => {
  const navigate = useNavigate();
  const { isLoggedIn, loadingAuth } = useContext(AuthContext);

  // States for title & description
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // State for video visibility (public or private)
  const [visibility, setVisibility] = useState('public');

  // States for selected files
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);

  // Preview of the selected thumbnail (as a Data URL)
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  // Upload message (success/error), progress indicator
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Redirect if not logged in
  useEffect(() => {
    if (!loadingAuth && !isLoggedIn) {
      navigate('/login');
    }
  }, [loadingAuth, isLoggedIn, navigate]);

  // Handle changes for title and description
  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };
  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  // Handle visibility change (public/private)
  const handleVisibilityChange = (e) => {
    setVisibility(e.target.value);
  };

  // Handle video selection
  const handleVideoChange = (event) => {
    setSelectedVideo(event.target.files[0]);
  };

  // Handle thumbnail selection & create preview
  const handleThumbnailChange = (event) => {
    const file = event.target.files[0];
    setSelectedThumbnail(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setThumbnailPreview(null);
    }
  };

  // Handle form submission
  const handleUpload = async (event) => {
    event.preventDefault();
    setUploadMessage('');
    setUploadProgress(0);

    // Validate required fields
    if (!title.trim()) {
      setUploadMessage('Title is required.');
      return;
    }
    if (!selectedVideo) {
      setUploadMessage('No video file selected!');
      return;
    }
    if (!selectedThumbnail) {
      setUploadMessage('No thumbnail file selected!');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description);
      formData.append('visibility', visibility);
      formData.append('movieFile', selectedVideo);
      formData.append('thumbnailFile', selectedThumbnail);

      const response = await apiRequest.post(
        '/movies/upload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percent);
            }
          },
        }
      );

      if (response.status === 200) {
        setUploadMessage(response.data.message || 'Upload successful!');
      } else {
        setUploadMessage(response.data.message || 'Upload failed. Please try again.');
      }
    } catch (error) {
      logger.error('Error uploading file:', error);
      setUploadMessage(
        error.response?.data?.message || 'Error uploading file. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  if (loadingAuth) {
    return <div className={styles.uploadLoading}>Checking authentication...</div>;
  }

  return (
    <div className={styles.uploadContainer}>
      <h2 className={styles.uploadTitle}>Upload Your Video</h2>

      {/* Warning / Guidelines Section */}
      <div className={styles.warningContainer}>
        <h3 className={styles.warningTitle}>Important Guidelines</h3>
        <p className={styles.warningText}>
          By uploading content, you confirm that all participants are 18 or older,
          you hold the rights to the content, and it does not violate any laws or guidelines.
          We strictly prohibit any illegal or non-consensual content, including minors or bestiality.
          Violating these rules will result in content removal and possible account termination.
        </p>
      </div>

      {uploadMessage && <p className={styles.uploadMessage}>{uploadMessage}</p>}

      <form onSubmit={handleUpload} className={styles.uploadForm}>
        {/* Title (Required) */}
        <label className={styles.inputLabel}>Title (Required)</label>
        <input
          type="text"
          value={title}
          onChange={handleTitleChange}
          placeholder="Enter a title"
          className={styles.textInput}
        />

        {/* Description (Optional) */}
        <label className={styles.inputLabel}>Description (Optional)</label>
        <textarea
          value={description}
          onChange={handleDescriptionChange}
          placeholder="Enter a short description (optional)"
          rows="3"
          className={styles.textInput}
          style={{ resize: 'vertical' }}
        />

        {/* Visibility selection */}
        <label className={styles.inputLabel}>Visibility</label>
        <select
          value={visibility}
          onChange={handleVisibilityChange}
          className={styles.selectInput}
        >
          <option value="public">Public</option>
          <option value="private">Private</option>
        </select>

        <label className={styles.inputLabel}>Select Video (Max 1GB)</label>
        <input
          type="file"
          onChange={handleVideoChange}
          accept="video/*"
          className={styles.fileInput}
        />

        <label className={styles.inputLabel}>Select Thumbnail (Max 2MB)</label>
        <input
          type="file"
          onChange={handleThumbnailChange}
          accept="image/*"
          className={styles.fileInput}
        />

        {/* Thumbnail preview */}
        {thumbnailPreview && (
          <div className={styles.thumbnailPreview}>
            <img src={thumbnailPreview} alt="Thumbnail Preview" />
          </div>
        )}

        {/* Uploading indicator: a spinner */}
        {uploading && (
          <div className={styles.spinnerContainer}>
            <div className={styles.spinner}></div>
            <p className={styles.uploadingText}>Uploading...</p>
          </div>
        )}

        <button
          type="submit"
          className={styles.uploadBtn}
          disabled={!title.trim() || !selectedVideo || !selectedThumbnail || uploading}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
      </form>
    </div>
  );
};

export default UploadPage;
